import React, {useEffect, useRef, useState} from "react";
import {FlowData, RegisterFunction} from "../../../../util/types.ts";
import {ScrollToAnchorPoint} from "../../../../util/ScrollToAnchor.tsx";
import {filterIllegalCharcters, scrollToElement} from "../../../../util/helpers.ts";

interface OwnHardwareProps {
    flowData: FlowData;
    setFlowData: React.Dispatch<React.SetStateAction<FlowData>>;
    register: RegisterFunction;
}

export default function OwnHardwareComponent({
        flowData,
        setFlowData,
        register,
    }: OwnHardwareProps) {

    const [hardwareError, setHardwareError] = useState<string>('');
    const [hardwareDescriptionError, setHardwareDescriptionError] = useState<string>('');

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value === 'yes';
        setFlowData((prevFlowData) => ({ ...prevFlowData, ownHardware: value }));

        if (value) {
            setHardwareError('');
        }
    };

    const handleTextFieldChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        let input = event.target.value;

        if (input.length > 500) {
            input = input.substring(0, 500);
        }

        const sanitizedInput = filterIllegalCharcters(input);

        setFlowData((prev) => ({ ...prev, ownHardwareDescription: sanitizedInput }));
        setHardwareDescriptionError('');
    };

    const hasError = flowData.ownHardware === null && hardwareError.length > 0;
    const hasDescriptionError = hardwareDescriptionError.length > 0;
    const ownHardwareComponentTop = useRef() as React.MutableRefObject<HTMLDivElement>;
    const ownHardwareDescriptionComponentTop = useRef() as React.MutableRefObject<HTMLDivElement>;

    useEffect((): void => {
        register("OwnHardwareComponent",
            {
                hasErrors: (flowData: FlowData): boolean => {

                    if (flowData.ownHardware === null) {
                        setHardwareError("Vælg om du bruger ekstraudstyr eller kun din Hiper-router.");
                        return true
                    }

                    if (!flowData.ownHardwareDescription?.trim() && flowData.ownHardware) {
                        setHardwareDescriptionError("Beskriv hvilket ekstraudstyr du bruger.");
                        return true
                    }
                    return false
                },
                scrollToError: (): void => {
                    if (flowData.ownHardware === null) {
                        scrollToElement(ownHardwareComponentTop.current)
                        return
                    }
                    if (!flowData.ownHardwareDescription?.trim() && flowData.ownHardware) {
                        scrollToElement(ownHardwareDescriptionComponentTop.current)
                        return
                    }
                },
            },
        )
    }, [register, hasDescriptionError, flowData.ownHardwareDescription, flowData.ownHardware]);

    return (
        <>
            <div className={`input-container${hasError ? ' error' : ''}`} ref={ownHardwareComponentTop}>
                <ScrollToAnchorPoint id={'own-hardware-section'} className={'id-anchor--selfcare-report-error'}/>
                <p>
                    Bruger du ekstraudstyr?*<br/>
                    (F.eks. wifi mesh eller en ekstern router)
                </p>
                <p className={'with-checkbox'}>
                    <input
                        type={'radio'}
                        id={'own_hardware_yes'}
                        name={'own_hardware'}
                        value={'yes'}
                        onChange={handleRadioChange}
                        checked={flowData.ownHardware === true}
                    />
                    <label
                        className={'checkbox-label checkbox-label--montserrat'}
                        htmlFor={'own_hardware_yes'}
                    >
                        Ja
                    </label>
                </p>
                <p className={'with-checkbox'}>
                    <input
                        type={'radio'}
                        id={'own_hardware_no'}
                        name={'own_hardware'}
                        value={'no'}
                        onChange={handleRadioChange}
                        checked={flowData.ownHardware === false}
                    />
                    <label
                        className={'checkbox-label checkbox-label--montserrat'}
                        htmlFor={'own_hardware_no'}
                    >
                        Nej, jeg bruger kun min Hiper-router
                    </label>
                </p>

                {hasError && <p className={'error-message'}>{hardwareError}</p>}
            </div>

            {flowData.ownHardware === true && (
                <div className={`input-container${hasDescriptionError ? ' error' : ''}`} ref={ownHardwareDescriptionComponentTop}>
                    <ScrollToAnchorPoint id={'own-hardware-description-section'} className={'id-anchor--selfcare-report-error'}/>
                    <p>
                        Beskriv hvilket ekstraudstyr du bruger*
                    </p>
                    <textarea
                        id={'own_hardware_description'}
                        name={'own_hardware_description'}
                        value={flowData.ownHardwareDescription}
                        onChange={handleTextFieldChange}
                        maxLength={500}
                        rows={4}
                        placeholder={'Beskriv udstyr'}
                    />
                    {hasDescriptionError && <p className={'error-message'}>{hardwareDescriptionError}</p>}
                </div>
            )}
        </>
    );
}
